/**
 * dispatch_view_controller.js — how the Dispatch board is laid out on this device (Stimulus).
 *
 * Hosted beside dispatch_controller.js on the page container in
 * templates/calendar/dispatch.html. Two choices, each remembered in localStorage
 * the way the calendar remembers month/week/day (calendar/state.js CALENDAR_VIEW_KEY):
 *
 *   LAYOUT  'across': time runs across, people are rows down the side. The
 *           default, because rows fit more of a big crew on screen, and it is
 *           how Simpro and most dispatch boards read. 'down': time runs down,
 *           people are columns.
 *   ZOOM    how much time the frame holds: out to see the whole day across the
 *           crew, in to read visits squeezed side by side. With time across, the
 *           day never holds less than the board's width (calendar.css), so the
 *           levels below that fill are skipped rather than offered as no-ops.
 *
 *   NOW     on today's board, where the time is: data-now and --now-slots, kept
 *           current each minute. The board opens at its first hour, the
 *           morning's work in view, unless now would be off that screen; then
 *           it is scrolled so now sits a quarter of the way in, the way a phone
 *           calendar opens on the current hour.
 *
 * A device with no remembered zoom opens a phone at the largest zoom that holds
 * a working day (WORKING_HOURS) across the screen, and anything wider at 1.
 *
 * This controller only writes data-time-axis, --board-zoom, data-now and
 * --now-slots onto the board; static/css/calendar.css draws them, and
 * dispatch_controller.js reads data-time-axis to turn a pointer into a time.
 * The board's first hour, slot length and slot count are dispatch_controller's
 * values on the same element (dispatch.html), read here rather than restated.
 */
import { Controller } from "@hotwired/stimulus";

const AXIS_KEY = 'dispatch_time_axis';
const ZOOM_KEY = 'dispatch_zoom';
const AXES = ['across', 'down'];
// Multiples of the slot size in calendar.css; 1 is the size the board was designed at.
const ZOOM_LEVELS = [0.5, 0.75, 1, 1.5, 2, 3];
const DEFAULT_ZOOM_INDEX = ZOOM_LEVELS.indexOf(1);
// calendar.css: a slot is 2rem at zoom 1, and a phone's name column is 2.75rem zoomed out.
const SLOT_REM = 2;
const PHONE_NAME_REM = 2.75;
const PHONE_QUERY = '(max-width: 768px)';
// The span a phone should see without scrolling: a trade's day, 7am to 5pm.
const WORKING_HOURS = 10;
const NOW_TICK_MS = 60 * 1000;
// Where now sits when it has to be scrolled to: this far into the visible lanes.
const NOW_LEAD = 0.25;

function read(key) {
    try { return localStorage.getItem(key); } catch { return null; }   // private mode
}

function write(key, value) {
    try { localStorage.setItem(key, value); } catch { /* the choice just won't persist */ }
}

export default class extends Controller {
    static targets = ["board", "axisButton", "zoomIn", "zoomOut"];

    connect() {
        const axis = read(AXIS_KEY);
        const zoom = ZOOM_LEVELS.indexOf(Number(read(ZOOM_KEY)));
        this._axis = AXES.includes(axis) ? axis : AXES[0];
        this._zoom = zoom === -1 ? this._defaultZoomIndex() : zoom;
        this._render();
        this._paintNow();
        this._scrollToNow();
        this._nowTimer = setInterval(() => this._paintNow(), NOW_TICK_MS);
        // The width the day fills moves with the window, and with it which zooms change anything.
        this._onResize = () => this._render();
        window.addEventListener('resize', this._onResize);
    }

    disconnect() {
        clearInterval(this._nowTimer);
        window.removeEventListener('resize', this._onResize);
    }

    setAxis(event) {
        const axis = event.currentTarget.dataset.axis;
        if (!AXES.includes(axis) || axis === this._axis) return;
        this._axis = axis;
        write(AXIS_KEY, axis);
        this._render();
    }

    /**
     * A phone's rows show initials to give the time axis the width; a tap on a
     * header shows everyone's name until the next tap. Not remembered: the
     * narrow column is the resting state. calendar.css widens the column on
     * data-names-open, and only at phone width.
     */
    toggleNames() {
        const board = this.boardTarget;
        if ('namesOpen' in board.dataset) delete board.dataset.namesOpen;
        else board.dataset.namesOpen = '';
    }

    /** The next level past what the board shows, which may be the fill rather than the level. */
    zoomIn() {
        this._zoomTo(ZOOM_LEVELS.findIndex((zoom) => zoom > this._shownZoom()));
    }

    zoomOut() {
        if (ZOOM_LEVELS[this._zoom] > this._fillZoom()) this._zoomTo(this._zoom - 1);
    }

    _zoomTo(index) {
        if (index < 0 || index >= ZOOM_LEVELS.length) return;
        this._keepingCentre(() => {
            this._zoom = index;
            this._render();
        });
        write(ZOOM_KEY, String(ZOOM_LEVELS[index]));
    }

    _render() {
        const board = this.boardTarget;
        board.dataset.timeAxis = this._axis;
        board.style.setProperty('--board-zoom', ZOOM_LEVELS[this._zoom]);
        this.axisButtonTargets.forEach((button) => {
            const on = button.dataset.axis === this._axis;
            button.classList.toggle('active', on);
            button.setAttribute('aria-pressed', String(on));
        });
        this.zoomOutTarget.disabled = this._zoom === 0 || ZOOM_LEVELS[this._zoom] <= this._fillZoom();
        this.zoomInTarget.disabled = !ZOOM_LEVELS.some((zoom) => zoom > this._shownZoom());
    }

    /**
     * The zoom at which the day exactly fills the board's width, with time across;
     * calendar.css never draws a slot smaller than that, so no level below it
     * changes anything. 0 with time down, where the day runs off the bottom.
     */
    _fillZoom() {
        const board = this.boardTarget;
        if (board.dataset.timeAxis !== 'across') return 0;
        const beside = ['.dispatch-axis > .dispatch-head', '.dispatch-axis > .dispatch-allday']
            .reduce((width, selector) => width + (board.querySelector(selector)?.offsetWidth || 0), 0);
        const rem = parseFloat(getComputedStyle(document.documentElement).fontSize);
        const slots = Number(this.element.dataset.dispatchSlotCountValue);
        return (board.clientWidth - beside) / slots / (SLOT_REM * rem);
    }

    _shownZoom() {
        return Math.max(ZOOM_LEVELS[this._zoom], this._fillZoom());
    }

    /** A phone: the largest zoom whose working day fits beside the name column. Else 1. */
    _defaultZoomIndex() {
        if (!window.matchMedia(PHONE_QUERY).matches) return DEFAULT_ZOOM_INDEX;
        const rem = parseFloat(getComputedStyle(document.documentElement).fontSize);
        const room = this.boardTarget.clientWidth - PHONE_NAME_REM * rem;
        const hourAt = (zoom) => 2 * SLOT_REM * rem * zoom;
        const fits = ZOOM_LEVELS.filter((zoom) => hourAt(zoom) * WORKING_HOURS <= room);
        return fits.length ? ZOOM_LEVELS.indexOf(fits[fits.length - 1]) : 0;
    }

    /** Slots since the board's first hour, or null when the board is not today or now is off it. */
    _nowSlots() {
        if (this.boardTarget.dataset.today !== 'true') return null;
        const data = this.element.dataset;
        const now = new Date();
        const minutes = now.getHours() * 60 + now.getMinutes() - Number(data.dispatchStartHourValue) * 60;
        const slots = minutes / Number(data.dispatchSlotMinutesValue);
        return slots >= 0 && slots <= Number(data.dispatchSlotCountValue) ? slots : null;
    }

    _paintNow() {
        const board = this.boardTarget;
        const slots = this._nowSlots();
        if (slots === null) {
            delete board.dataset.now;
            board.style.removeProperty('--now-slots');
            return;
        }
        board.dataset.now = '';
        board.style.setProperty('--now-slots', slots.toFixed(3));
    }

    /** Scroll today's board to now, when now is not on the opening screen. */
    _scrollToNow() {
        const slots = this._nowSlots();
        const board = this.boardTarget;
        const lane = board.querySelector('.dispatch-lane');
        if (slots === null || !lane) return;
        const across = board.dataset.timeAxis === 'across';
        const [scroll, view, edge, size] = across
            ? ['scrollLeft', board.clientWidth, 'left', 'width']
            : ['scrollTop', board.clientHeight, 'top', 'height'];
        const laneRect = lane.getBoundingClientRect();
        // Unscrolled, the lane starts after the sticky header, which never scrolls away.
        const header = laneRect[edge] - board.getBoundingClientRect()[edge] + board[scroll];
        const visible = view - header;
        const now = laneRect[size] * slots / Number(this.element.dataset.dispatchSlotCountValue);
        if (now <= visible) return;
        board[scroll] = now - visible * NOW_LEAD;
    }

    /**
     * Run `change` (a zoom) and scroll so the time that was in the middle of the
     * frame is still there, rather than the board sliding away from what you
     * were looking at.
     */
    _keepingCentre(change) {
        const board = this.boardTarget;
        const lane = board.querySelector('.dispatch-lane');
        if (!lane) {
            change();
            return;
        }
        const across = board.dataset.timeAxis === 'across';
        const [scroll, view, edge, size] = across
            ? ['scrollLeft', board.clientWidth, 'left', 'width']
            : ['scrollTop', board.clientHeight, 'top', 'height'];
        const laneStart = lane.getBoundingClientRect()[edge] - board.getBoundingClientRect()[edge] + board[scroll];
        const centre = (board[scroll] + view / 2 - laneStart) / lane.getBoundingClientRect()[size];
        change();
        board[scroll] = laneStart + centre * lane.getBoundingClientRect()[size] - view / 2;
    }
}
